/**
 * Knex query helpers for repository adapters.
 *
 * Pure functions over Knex.QueryBuilder — no side effects, no service deps.
 * Extracted from BaseEntityService so all Knex repos share the same logic.
 */

import type { Knex } from 'knex'
import type { EntityQuery, FieldDefinition } from '@gzl10/nexus-sdk'
import { applyFilters as applyFiltersHelper } from '../../../db/filter-helpers.js'

// Re-export so consumers import from a single place
export { applyFiltersHelper as applyFilters }

// ============================================================================
// Sorting
// ============================================================================

export interface SortOptions {
  /** Default sort applied when the query carries no explicit sort. */
  defaultSort?: { field: string; order: 'asc' | 'desc' } | null
  /** Fallback: sort by this field ASC when neither query.sort nor defaultSort is set. */
  labelField?: string
}

/**
 * Apply sorting to a Knex query builder.
 *
 * Priority: query.sort > options.defaultSort > options.labelField (asc).
 * If none is present, query builder is returned unchanged.
 */
export function applySorting(
  qb: Knex.QueryBuilder,
  query?: EntityQuery,
  options?: SortOptions
): Knex.QueryBuilder {
  if (query?.sort) {
    qb.orderBy(query.sort, query.order ?? 'asc')
  } else if (options?.defaultSort) {
    qb.orderBy(options.defaultSort.field, options.defaultSort.order)
  } else if (options?.labelField) {
    qb.orderBy(options.labelField, 'asc')
  }
  return qb
}

// ============================================================================
// Pagination
// ============================================================================

/**
 * Apply pagination to a Knex query builder.
 *
 * limit=0 means unpaginated — no LIMIT/OFFSET is added.
 */
export function applyPagination(
  qb: Knex.QueryBuilder,
  limit: number,
  offset: number
): Knex.QueryBuilder {
  if (limit > 0) {
    qb.limit(limit).offset(offset)
  }
  return qb
}

// ============================================================================
// Search
// ============================================================================

/**
 * Apply text search across the given fields.
 *
 * - Fields with db.type === 'json' use `CAST(?? AS TEXT) LIKE ?` (SQLite/Postgres compatible).
 * - Other fields use plain `LIKE` (case-insensitive in SQLite, use ILIKE in Postgres adapters).
 * - If no searchableFields provided → no-op.
 */
export function applySearchOnFields(
  qb: Knex.QueryBuilder,
  search: string,
  searchableFields: string[],
  fieldDefs?: Record<string, FieldDefinition>
): Knex.QueryBuilder {
  if (searchableFields.length === 0) return qb

  const searchTerm = `%${search}%`

  qb.where(function () {
    for (const fieldName of searchableFields) {
      const field = fieldDefs?.[fieldName]
      if (field?.db?.type === 'json') {
        this.orWhereRaw(`CAST(?? AS TEXT) LIKE ?`, [fieldName, searchTerm])
      } else {
        this.orWhere(fieldName, 'like', searchTerm)
      }
    }
  })

  return qb
}
